import React from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { useHits } from 'react-instantsearch';
import type { QueryDslBoolQuery, QueryDslQueryContainer } from '@elastic/elasticsearch/lib/api/types';

import type { Langcodes } from '@uncinc/react-drupal-core/types';

import getInitialFacetUiState from '../../helpers/getInitialFacetUiState';
import { APP_OPTIONS } from '../../config/constants';
import SearchAllContentSearchBar from '../SearchAllContent/SearchBar';
import type { SearchAllContentFacet } from '../SearchAllContent/types';
import MetaSearch, { type MetaSearchProps } from '../Search/MetaSearch';
import { constructQuery, getSearchbarPlaceholderKey } from '../Search/MetaSearch/metaParagraphsHelpers';

import './style.scss';

export interface RelatedContentProps extends Pick<MetaSearchProps, 'contentType' | 'hitsPerPage'> {
  title?: string;
  filterNames: string[];
  filterValues: (string | number)[];
  facets?: SearchAllContentFacet[];
  withSearchbar?: boolean;
  langcode: Langcodes,
  className?: string;
}

const RelatedContentInner: React.FC<Pick<
RelatedContentProps,
'title' | 'contentType' | 'className' | 'facets' | 'withSearchbar'
>> = (props) => {
  const {
    title,
    contentType,
    className,
    facets,
    withSearchbar,
  } = props;

  const { t } = useTranslation();
  const classes = c('RelatedContent', `RelatedContent--${contentType}`, className);

  const { hits } = useHits();

  if (!hits || hits.length === 0) {
    return null;
  }

  return (
    <div className={classes}>
      {title && (
        <h2 className="RelatedContent__title">
          {title}
        </h2>
      )}

      {withSearchbar && (
        <SearchAllContentSearchBar
          className="RelatedContent__SearchBar"
          title={t('searchkit.search_bar.title')}
          placeholder={t(getSearchbarPlaceholderKey(contentType))}
        />
      )}

      {facets && (
        <MetaSearch.Facets facets={facets} />
      )}
      <MetaSearch.Inner contentType={contentType} withPagination disableScrollTo />
    </div>
  );
};

const RelatedContent: React.FC<RelatedContentProps> = (props) => {
  const {
    title,
    contentType,
    hitsPerPage,
    filterNames,
    filterValues,
    facets,
    withSearchbar,
    className,
    langcode,
  } = props;

  const filters: QueryDslQueryContainer[] = [];
  filterNames.forEach((filterName) => filters.push(
    {
      terms: {
        [filterName]: filterValues,
      },
    },
  ));

  const requestParameters: QueryDslBoolQuery = {
    should: filters,
  };

  return (
    <MetaSearch
      contentType={contentType}
      hitsPerPage={hitsPerPage}
      requestOptions={{
        getBaseFilters: () => [
          constructQuery(contentType, langcode),
          {
            bool: requestParameters,
          },
        ],
      }}
      {...(facets ? {
        initialUiState: {
          [APP_OPTIONS.parameters.searchkitIndexAllContent]: getInitialFacetUiState(facets),
        },
      } : {})}
    >
      <RelatedContentInner
        title={title}
        contentType={contentType}
        className={className}
        facets={facets}
        withSearchbar={withSearchbar}
      />
    </MetaSearch>
  );
};

export default RelatedContent;
